import { addDays, addMonths, startOfDay, isSameDay, format, endOfMonth } from 'date-fns';
import { RecurringPayment, OneOffPayment, ForecastEntry, Account, PaydayRule, Pot, CreditCard } from '../types';

export const calculatePaymentDueDate = (dayOfMonth: number | 'LDOM', fromDate: Date): Date => {
  const currentMonth = fromDate.getMonth();
  const currentYear = fromDate.getFullYear();
  
  let dueDate: Date;
  
  if (dayOfMonth === 'LDOM') {
    dueDate = endOfMonth(new Date(currentYear, currentMonth));
  } else {
    dueDate = new Date(currentYear, currentMonth, dayOfMonth);
  }
  
  if (dueDate < fromDate) {
    if (dayOfMonth === 'LDOM') {
      dueDate = endOfMonth(addMonths(dueDate, 1));
    } else {
      dueDate = addMonths(dueDate, 1);
    }
  }
  
  return startOfDay(dueDate);
};

export const getNextPaymentDate = (payment: RecurringPayment, fromDate: Date): Date => {
  return calculatePaymentDueDate(payment.dayOfMonth, fromDate);
};

export const generateRecurringPayments = (
  payments: RecurringPayment[],
  startDate: Date,
  endDate: Date
): ForecastEntry[] => {
  const entries: ForecastEntry[] = [];

  payments.filter(p => p.isActive).forEach(payment => {
    // Determine the effective start date for this payment
    const paymentStartDate = payment.startDate
      ? startOfDay(payment.startDate)
      : new Date(0); // Beginning of time if no start date

    // If the payment hasn't started yet, use the payment start date
    const effectiveStartDate = paymentStartDate > startDate ? paymentStartDate : startDate;

    let currentDate = getNextPaymentDate(payment, effectiveStartDate);

    // If this is the first payment and we have a start date, make sure we don't schedule before it
    if (payment.startDate && currentDate < paymentStartDate) {
      currentDate = getNextPaymentDate(payment, paymentStartDate);
    }

    // Determine the effective end date for this payment
    const paymentEndDate = payment.endDate
      ? startOfDay(payment.endDate)
      : endDate;

    // Use the earlier of forecast end date or payment end date
    const effectiveEndDate = paymentEndDate < endDate ? paymentEndDate : endDate;

    while (currentDate <= effectiveEndDate) {
      // Only add the payment if it's after the payment start date
      if (!payment.startDate || currentDate >= paymentStartDate) {
        const amount = payment.type === 'income' ? payment.amount : -payment.amount;
        entries.push({
          date: currentDate,
          description: payment.description,
          amount,
          balance: 0, // Will be calculated later
          type: 'recurring'
        });
      }

      // For LDOM, we need to get the end of the next month
      if (payment.dayOfMonth === 'LDOM') {
        currentDate = endOfMonth(addMonths(currentDate, 1));
      } else {
        currentDate = addMonths(currentDate, 1);
      }
    }
  });

  return entries;
};

export const generateOneOffPayments = (
  payments: OneOffPayment[],
  startDate: Date,
  endDate: Date
): ForecastEntry[] => {
  return payments
    .filter(payment => {
      const paymentDate = startOfDay(payment.date);
      return paymentDate >= startDate && paymentDate <= endDate;
    })
    .map(payment => {
      const amount = payment.type === 'income' ? payment.amount : -payment.amount;
      return {
        date: startOfDay(payment.date),
        description: payment.description,
        amount,
        balance: 0, // Will be calculated later
        type: 'oneoff' as const
      };
    });
};

export const generateCreditCardEntries = (
  creditCards: CreditCard[],
  startDate: Date,
  endDate: Date
): ForecastEntry[] => {
  const entries: ForecastEntry[] = [];
  const today = startOfDay(new Date());

  creditCards.filter(card => card.isActive && card.currentBalance > 0).forEach(card => {
    let paymentDate = calculatePaymentDueDate(card.paymentDayOfMonth, startDate);

    // For the first payment only, use the current balance
    // After that, assume the balance will be zero (user will update manually for new bills)
    let isFirstPayment = true;

    while (paymentDate <= endDate) {
      if (isFirstPayment) {
        // First payment uses the current balance (this month's bill)
        entries.push({
          date: paymentDate,
          description: `${card.name}`,
          amount: -card.currentBalance,
          balance: 0, // Will be calculated later
          type: 'creditcard'
        });
        isFirstPayment = false;
      }
      // After the first payment, we don't add entries because the balance would be zero
      // Users need to manually update the balance for future months

      // Move to next month
      if (card.paymentDayOfMonth === 'LDOM') {
        paymentDate = endOfMonth(addMonths(paymentDate, 1));
      } else {
        paymentDate = addMonths(paymentDate, 1);
      }
    }
  });

  return entries;
};

export const generatePaydayEntries = (
  paydayRules: PaydayRule[],
  pots: Pot[],
  startDate: Date,
  endDate: Date
): ForecastEntry[] => {
  const entries: ForecastEntry[] = [];
  
  // Group all payday rules by their day of month
  const rulesByDay = new Map<number | 'LDOM', PaydayRule[]>();
  paydayRules.filter(rule => rule.isActive).forEach(rule => {
    if (!rulesByDay.has(rule.dayOfMonth)) {
      rulesByDay.set(rule.dayOfMonth, []);
    }
    rulesByDay.get(rule.dayOfMonth)!.push(rule);
  });
  
  // Process each unique payday date
  rulesByDay.forEach((rules, dayOfMonth) => {
    let paymentDate = calculatePaymentDueDate(dayOfMonth, startDate);
    
    while (paymentDate <= endDate) {
      const currentPaymentDate = paymentDate;
      
      // Collect all allocations for this date, respecting date ranges
      const allAllocations: Array<{pot: Pot, amount: number}> = [];
      rules.forEach(rule => {
        // Check if the rule is active for this specific date
        const ruleStartDate = rule.startDate ? startOfDay(rule.startDate) : new Date(0);
        const ruleEndDate = rule.endDate ? startOfDay(rule.endDate) : new Date(8640000000000000); // Max safe date

        if (currentPaymentDate >= ruleStartDate && currentPaymentDate <= ruleEndDate) {
          rule.allocations.forEach(allocation => {
            const pot = pots.find(p => p.id === allocation.potId);
            if (pot) {
              allAllocations.push({pot, amount: allocation.amount});
            }
          });
        }
      });
      
      // Create a single grouped entry for this payday
      if (allAllocations.length > 0) {
        const totalAmount = allAllocations.reduce((sum, alloc) => sum + alloc.amount, 0);
        const potNames = allAllocations.map(alloc => alloc.pot.name).join(', ');
        
        entries.push({
          date: currentPaymentDate,
          description: `Payday transfers to ${potNames}`,
          amount: -totalAmount,
          balance: 0, // Will be calculated later
          type: 'payday'
        });
      }
      
      // For LDOM, we need to get the end of the next month
      if (dayOfMonth === 'LDOM') {
        paymentDate = endOfMonth(addMonths(paymentDate, 1));
      } else {
        paymentDate = addMonths(paymentDate, 1);
      }
    }
  });
  
  return entries;
};

export const calculateForecast = (
  account: Account,
  recurringPayments: RecurringPayment[],
  oneOffPayments: OneOffPayment[],
  paydayRules: PaydayRule[],
  pots: Pot[],
  creditCards: CreditCard[],
  days: number = 31
): ForecastEntry[] => {
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const endDate = addDays(today, days);

  let allEntries: ForecastEntry[] = [];

  allEntries.push(...generateRecurringPayments(recurringPayments, tomorrow, endDate));
  allEntries.push(...generateOneOffPayments(oneOffPayments, tomorrow, endDate));
  allEntries.push(...generatePaydayEntries(paydayRules, pots, tomorrow, endDate));
  allEntries.push(...generateCreditCardEntries(creditCards, tomorrow, endDate));
  
  allEntries.sort((a, b) => a.date.getTime() - b.date.getTime());
  
  let runningBalance = account.currentBalance;
  
  allEntries.forEach(entry => {
    runningBalance += entry.amount;
    entry.balance = runningBalance;
  });
  
  return allEntries;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount);
};

export const formatDate = (date: Date): string => {
  return format(date, 'dd/MM/yyyy');
};

export const formatDateShort = (date: Date): string => {
  return format(date, 'dd MMM');
};

export const isPaymentDueToday = (payment: RecurringPayment): boolean => {
  const today = new Date();
  const dueDate = getNextPaymentDate(payment, today);
  return isSameDay(dueDate, today);
};

export const getDaysUntilPayment = (payment: RecurringPayment): number => {
  const today = startOfDay(new Date());
  const dueDate = getNextPaymentDate(payment, today);
  const diffTime = dueDate.getTime() - today.getTime();
  const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // If the payment is due today, it has already happened at 00:00
  // so the next payment is actually next month
  if (daysUntil === 0) {
    const nextDueDate = getNextPaymentDate(payment, addDays(today, 1));
    const nextDiffTime = nextDueDate.getTime() - today.getTime();
    return Math.ceil(nextDiffTime / (1000 * 60 * 60 * 24));
  }

  return daysUntil;
};

export const getPaymentTypeInfo = (entry: ForecastEntry) => {
  switch (entry.type) {
    case 'recurring':
      return { label: '🔄 Recurring', className: 'pill pill-recurring' };
    case 'oneoff':
      return { label: '📅 One-off', className: 'pill pill-oneoff' };
    case 'creditcard':
      return { label: '💳 Credit Card', className: 'pill pill-creditcard' };
    case 'payday':
      return { label: '💸 Payday', className: 'pill pill-payday' };
    default:
      return { label: '❓ Unknown', className: 'pill pill-info' };
  }
};

